import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/site";

const field = "bg-[#0b0b0b] border-white/15 focus-visible:ring-gold/40 text-white placeholder:text-white/30 rounded-none h-12";

export default function Contact() {
  const { hash } = useLocation();
  const [inq, setInq] = useState({ name: "", email: "", plan_interest: "", message: "" });
  const [app, setApp] = useState({ name: "", email: "", platform_handle: "", followers: "", niche: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [busyApp, setBusyApp] = useState(false);

  useEffect(() => {
    if (hash === "#apply") {
      setTimeout(() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [hash]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!inq.name || !inq.email || !inq.message) return toast.error("Please fill in all required fields.");
    try {
      setBusy(true);
      await api.post("/inquiries", inq);
      toast.success("Message sent! We'll be in touch soon.");
      setInq({ name: "", email: "", plan_interest: "", message: "" });
    } catch { toast.error("Something went wrong. Try again."); }
    finally { setBusy(false); }
  };

  const submitApp = async (e) => {
    e.preventDefault();
    if (!app.name || !app.email || !app.message) return toast.error("Please fill in all required fields.");
    try {
      setBusyApp(true);
      await api.post("/applications", app);
      toast.success("Application received! We review these personally and reply to those selected.");
      setApp({ name: "", email: "", platform_handle: "", followers: "", niche: "", message: "" });
    } catch { toast.error("Something went wrong. Try again."); }
    finally { setBusyApp(false); }
  };

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6 text-center" data-testid="contact-hero">
        <Reveal>
          <p className="overline text-gold mb-4">Get In Touch</p>
          <h1 className="font-display text-6xl md:text-8xl uppercase text-white">Let's Talk</h1>
          <p className="mt-5 text-white/60 text-lg max-w-2xl mx-auto">Questions about a plan or want to work together? Send us a message.</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-8">
            <Reveal>
              <h2 className="font-display text-4xl uppercase text-white mb-6">Contact</h2>
              <div className="space-y-5">
                <div className="flex items-center gap-4 text-white/75"><Mail className="text-gold" size={20} /> contact@fanf0cus.com</div>
                <div className="flex items-center gap-4 text-white/75"><MapPin className="text-gold" size={20} /> Worldwide · Remote-first</div>
              </div>
              <div className="mt-10 border border-white/10 p-6">
                <p className="text-white/60 text-sm leading-relaxed">
                  <span className="text-gold font-medium">Revenue Share Partnership?</span> Use the application form below. This program is selective — we review every application personally.
                </p>
              </div>
            </Reveal>
          </div>

          {/* General inquiry */}
          <div className="lg:col-span-8">
            <Reveal>
              <form onSubmit={submitInquiry} className="border border-white/10 p-8 bg-[#0a0a0a]" data-testid="inquiry-form">
                <h3 className="font-display text-3xl uppercase text-white mb-6">Send A Message</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input data-testid="inq-name" className={field} placeholder="Your name *" value={inq.name} onChange={(e) => setInq({ ...inq, name: e.target.value })} />
                  <Input data-testid="inq-email" type="email" className={field} placeholder="Email *" value={inq.email} onChange={(e) => setInq({ ...inq, email: e.target.value })} />
                </div>
                <Input data-testid="inq-plan" className={`${field} mt-4`} placeholder="Plan of interest (optional)" value={inq.plan_interest} onChange={(e) => setInq({ ...inq, plan_interest: e.target.value })} />
                <Textarea data-testid="inq-message" className={`${field} h-36 mt-4 py-3`} placeholder="Your message *" value={inq.message} onChange={(e) => setInq({ ...inq, message: e.target.value })} />
                <button type="submit" disabled={busy} data-testid="inq-submit" className="mt-5 inline-flex items-center gap-2 bg-gold text-black px-8 py-3.5 overline hover:bg-[#F5D061] transition-colors duration-300 disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <>Send Message <Send size={15} /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>

        {/* Revenue share application */}
        <div id="apply" className="scroll-mt-28 mt-16">
          <Reveal>
            <form onSubmit={submitApp} className="border border-gold/30 p-8 md:p-10 bg-gradient-to-br from-[#171203]/60 to-[#0a0a0a]" data-testid="application-form">
              <p className="overline text-gold mb-3">Selective · Application Only</p>
              <h3 className="font-display text-4xl md:text-5xl uppercase text-white mb-3">Revenue Share Partnership</h3>
              <p className="text-white/60 max-w-2xl mb-8">No upfront fee. For serious creators who genuinely can't pay yet. Fan Focus receives 20–30% of revenue generated during a minimum 6-month term. Tell us why you're a fit.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="app-name" className={field} placeholder="Full name *" value={app.name} onChange={(e) => setApp({ ...app, name: e.target.value })} />
                <Input data-testid="app-email" type="email" className={field} placeholder="Email *" value={app.email} onChange={(e) => setApp({ ...app, email: e.target.value })} />
                <Input data-testid="app-handle" className={field} placeholder="Main platform / handle" value={app.platform_handle} onChange={(e) => setApp({ ...app, platform_handle: e.target.value })} />
                <Input data-testid="app-followers" className={field} placeholder="Current following (approx.)" value={app.followers} onChange={(e) => setApp({ ...app, followers: e.target.value })} />
              </div>
              <Input data-testid="app-niche" className={`${field} mt-4`} placeholder="Your niche / content focus" value={app.niche} onChange={(e) => setApp({ ...app, niche: e.target.value })} />
              <Textarea data-testid="app-message" className={`${field} h-36 mt-4 py-3`} placeholder="Why are you a fit, and what are your goals? *" value={app.message} onChange={(e) => setApp({ ...app, message: e.target.value })} />
              <button type="submit" disabled={busyApp} data-testid="app-submit" className="mt-5 inline-flex items-center gap-2 border border-gold text-gold px-8 py-3.5 overline hover:bg-gold hover:text-black transition-colors duration-300 disabled:opacity-60">
                {busyApp ? <Loader2 size={16} className="animate-spin" /> : <>Submit Application <Send size={15} /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
